import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { formatCount, formatPct, formatSeconds, formatWhen } from '../../../../lib/kyckConnect/insightFormat';
import { PORTAL_LEADS_PATH } from '../../../../lib/kyckConnect/portalLinks';
import { DASHBOARD_DAYS, type DashboardDays, type KyckConnectDashboard } from '../../../../types/kyckConnectInsights';
import { KcPage } from './KcPage';
import { fetchPortalDashboard, fetchPortalKcClubs } from './portalSetupApi';

// THE CLUB'S KYCKCONNECT DASHBOARD (Phase 3). SERVICE-ADAPTIVE: a block renders only when the server sent it, and the server sends one
// only for a service the selected clubs have. The page never decides what a club has and never computes a figure: it prints what
// arrived, and a missing figure is a dash.

function Stat({ label, value, testId, note }: { label: string; value: string; testId?: string; note?: string }) {
  return (
    <div className="kc-card rounded-[18px] border border-border p-3" data-testid={testId}>
      <div className="kc-label text-muted-foreground">{label}</div>
      <div className="kc-numeral mt-1 text-2xl font-semibold text-foreground">{value}</div>
      {note && <div className="text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}

function Block({ title, testId, children }: { title: string; testId: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h2 className="kc-label text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Bars({ rows }: { rows: Array<{ label: string; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <p className="text-muted-foreground">Nothing yet in this period.</p>;
  return (
    <ul className="space-y-1">
      {rows.map((r) => (
        <li key={r.label} className="flex items-center gap-2">
          <span className="w-40 shrink-0 truncate text-foreground">{r.label}</span>
          <span className="h-3 rounded-sm bg-primary" style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }} />
          <span className="tabular-nums text-muted-foreground">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

function PerDay({ rows }: { rows: Array<{ date: string; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="flex h-16 items-end gap-px" role="img" aria-label="Leads per day" data-testid="dash-per-day">
      {rows.map((r) => (
        <span key={r.date} title={`${r.date}: ${r.count}`} className="flex-1 rounded-t-sm bg-primary" style={{ height: `${r.count === 0 ? 2 : Math.max(6, (r.count / max) * 100)}%`, opacity: r.count === 0 ? 0.25 : 1 }} />
      ))}
    </div>
  );
}

export function DashboardBody({ d }: { d: KyckConnectDashboard }) {
  const none = !d.relay && !d.aiSms && !d.speedToLead;
  return (
    <div className="space-y-6">
      {d.capped && <p className="kc-card rounded-[12px] border border-border p-2 text-sm text-foreground" data-testid="dash-capped">This period holds more than we can total at once, so these figures are a minimum.</p>}
      {none && <p className="text-muted-foreground" data-testid="dash-no-services">No KyckConnect service is switched on for this club yet.</p>}

      {d.relay && (
        <Block title="Lead relay" testId="dash-relay">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Enquiries" value={formatCount(d.relay.leads)} note="Every enquiry received counts, repeats included" testId="dash-relay-leads" />
            <Stat label="Delivered to your CRM" value={formatPct(d.relay.deliveredPct)} note={d.relay.failed > 0 ? `${d.relay.failed} need attention` : undefined} testId="dash-relay-delivered" />
            <Stat label="In progress" value={formatCount(d.relay.inProgress)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="kc-label text-muted-foreground">By source</h3>
              <Bars rows={d.relay.bySource.map((s) => ({ label: s.source, count: s.count }))} />
            </div>
            <div>
              <h3 className="kc-label text-muted-foreground">Enquiries per day</h3>
              <PerDay rows={d.relay.perDay} />
            </div>
          </div>
        </Block>
      )}

      {d.aiSms && (
        <Block title={d.services.aiSms.botName ? `AI SMS (${d.services.aiSms.botName})` : 'AI SMS'} testId="dash-ai-sms">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Leads texted" value={formatCount(d.aiSms.leadsTexted)} />
            <Stat label="Replied" value={formatPct(d.aiSms.repliedPct)} note={`${d.aiSms.replied} replied`} />
            <Stat label="Booked by text" value={formatCount(d.aiSms.bookedByText)} />
            <Stat label="Answered after hours" value={formatCount(d.aiSms.afterHoursAnswered)} />
            <Stat label="Median first text" value={formatSeconds(d.aiSms.medianFirstTextSeconds)} />
          </div>
        </Block>
      )}

      {d.speedToLead && (
        <Block title="Speed to Lead" testId="dash-speed">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Median speed to call" value={formatSeconds(d.speedToLead.medianSpeedSeconds)} note={`over ${d.speedToLead.respondedCount} leads`} />
            <Stat label="Leads reached" value={formatPct(d.speedToLead.reachedPct)} note={`${d.speedToLead.reached} of ${d.speedToLead.calls}`} />
            <Stat label="Calls that booked" value={formatCount(d.speedToLead.booked)} />
            <Stat label="Calls your team made" value={formatCount(d.speedToLead.teamCalls)} note="started from a lead; not in the figures above" />
            <Stat label="Staff answered first round" value={formatPct(d.speedToLead.staffAnswer.pct)} note={`${d.speedToLead.staffAnswer.answered} of ${d.speedToLead.staffAnswer.of}`} />
          </div>
          {d.speedToLead.outOfHours > 0 && (
            <p className="text-sm text-muted-foreground" data-testid="dash-out-of-hours">
              {formatCount(d.speedToLead.outOfHours)} arrived out of hours, not called (not counted in leads reached).
            </p>
          )}
          <div>
            <h3 className="kc-label text-muted-foreground">How the calls ended</h3>
            <Bars rows={d.speedToLead.results.map((r) => ({ label: r.label, count: r.count }))} />
          </div>
        </Block>
      )}

      <Block title="Needs your team" testId="dash-needs">
        {d.needs.count === 0 ? (
          <p className="text-muted-foreground">Nobody is waiting on your team.</p>
        ) : (
          <>
            <p className="text-foreground"><span className="font-semibold">{d.needs.count}</span> {d.needs.count === 1 ? 'lead needs' : 'leads need'} a person.</p>
            <ul className="space-y-1">
              {d.needs.top.map((n) => (
                <li key={n.leadId}>
                  <Link className="underline" to={`${PORTAL_LEADS_PATH}?lead=${encodeURIComponent(n.leadId)}`}>{n.name ?? 'A lead'}{n.clubName ? ` (${n.clubName})` : ''}</Link>
                  {n.distress && <span className="kc-pill ml-2">Urgent</span>}
                </li>
              ))}
            </ul>
            <Link className="underline" to={PORTAL_LEADS_PATH}>See all leads</Link>
          </>
        )}
      </Block>

      <Block title="Upcoming bookings" testId="dash-upcoming">
        {d.upcoming.length === 0 ? (
          <p className="text-muted-foreground">No bookings coming up.</p>
        ) : (
          <ul className="space-y-1">
            {d.upcoming.map((u) => (
              <li key={u.appointmentId}>{formatWhen(u.startsAt, u.timezone)}: {u.leadName}{u.clubName ? ` (${u.clubName})` : ''}</li>
            ))}
          </ul>
        )}
      </Block>
    </div>
  );
}

export function PortalDashboardPage() {
  const [days, setDays] = useState<DashboardDays>(30);
  const [club, setClub] = useState<string | null>(null);
  const clubs = useQuery({ queryKey: ['portal-kc', 'clubs'], queryFn: fetchPortalKcClubs, retry: false });
  const dash = useQuery({ queryKey: ['portal-kc', 'dashboard', days, club], queryFn: () => fetchPortalDashboard(days, club), retry: false });
  const list = clubs.data?.clubs ?? [];
  return (
    <KcPage title="Dashboard" intro="How your leads are being handled, for the period you pick." testId="portal-dashboard-theme">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1" role="group" aria-label="Period">
          {DASHBOARD_DAYS.map((n) => (
            <button key={n} type="button" aria-pressed={days === n} className="kc-tab kc-chip" data-active={days === n} onClick={() => setDays(n)}>{n} days</button>
          ))}
        </div>
        {list.length > 1 && (
          <select aria-label="Club" className="kc-field" value={club ?? ''} onChange={(e) => setClub(e.target.value || null)}>
            <option value="">All my clubs</option>
            {list.map((c) => <option key={c.accountId} value={c.accountId}>{c.name}</option>)}
          </select>
        )}
      </div>
      {dash.isLoading ? <CenteredSpinner /> : dash.isError || !dash.data ? <p className="text-muted-foreground">We could not load the dashboard. Try again in a moment.</p> : <DashboardBody d={dash.data} />}
    </KcPage>
  );
}
